import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Crown, Trophy, Calendar, TrendingUp } from 'lucide-react';
import { format, parseISO, isFuture } from 'date-fns';
import { de } from 'date-fns/locale';
import { Link } from 'react-router-dom';

// SC Brühl Logo
const SC_BRUEHL_LOGO = 'https://scbruehl.ch/wp-content/themes/scbruehl/assets/img/scb_logo.svg';

export default function DashboardPage() {
    const { user, api } = useAuth();
    const [leaderboard, setLeaderboard] = useState([]);
    const [matches, setMatches] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [leaderboardRes, matchesRes] = await Promise.all([
                    api().get('/leaderboard'),
                    api().get('/matches')
                ]);
                setLeaderboard(leaderboardRes.data);
                setMatches(matchesRes.data);
            } catch (error) {
                console.error('Failed to fetch data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [api]);

    const upcomingMatches = matches.filter(m => !m.is_finished).slice(0, 3);
    const recentResults = matches.filter(m => m.is_finished).slice(-3).reverse();
    const userRank = leaderboard.find(e => e.user_id === user?.id)?.rank || '-';
    const top3 = leaderboard.slice(0, 3);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="spinner" />
            </div>
        );
    }

    return (
        <div className="space-y-8 animate-fade-in" data-testid="dashboard">
            {/* Welcome Section */}
            <div className="hero-background rounded-2xl p-8 text-white">
                <div className="flex items-center gap-4 mb-4">
                    <img 
                        src={SC_BRUEHL_LOGO} 
                        alt="SC Brühl" 
                        className="w-14 h-14 object-contain"
                    />
                    <div>
                        <h1 className="text-3xl font-heading">Willkommen, {user?.first_name}!</h1>
                        <p className="text-green-200">Rang #{userRank} mit {user?.total_points} Punkten</p>
                    </div>
                </div>
            </div>

            {/* Bento Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Top 3 Podium */}
                <Card className="md:col-span-2 lg:col-span-1 card-hover" data-testid="leaderboard-preview">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Trophy className="w-5 h-5 text-amber-500" />
                            Top 3 Rangliste
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {top3.map((entry, idx) => (
                                <div 
                                    key={entry.user_id}
                                    className={`flex items-center gap-3 p-3 rounded-lg ${
                                        idx === 0 ? 'bg-amber-50 dark:bg-amber-950/30' :
                                        idx === 1 ? 'bg-slate-50 dark:bg-slate-800/50' :
                                        'bg-orange-50 dark:bg-orange-950/30'
                                    }`}
                                >
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                        idx === 0 ? 'podium-first text-amber-950' :
                                        idx === 1 ? 'podium-second text-slate-800' :
                                        'podium-third text-orange-950'
                                    }`}>
                                        {entry.rank}
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-medium">{entry.pseudonym}</p>
                                        <p className="text-sm text-muted-foreground">{entry.predictions_count} Tipps</p>
                                    </div>
                                    <div className="text-right">
                                        <span className="text-xl font-heading tabular-nums">{entry.total_points}</span>
                                        <span className="text-sm text-muted-foreground ml-1">Pkt</span>
                                    </div>
                                </div>
                            ))}
                            {top3.length === 0 && (
                                <p className="text-muted-foreground text-center py-4">Noch keine Einträge</p>
                            )}
                        </div>
                        <Link 
                            to="/leaderboard" 
                            className="block mt-4 text-center text-primary hover:underline text-sm font-medium"
                            data-testid="view-full-leaderboard"
                        >
                            Vollständige Rangliste anzeigen
                        </Link>
                    </CardContent>
                </Card>

                {/* Upcoming Matches */}
                <Card className="card-hover" data-testid="upcoming-matches">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Calendar className="w-5 h-5 text-primary" />
                            Nächste Spiele
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {upcomingMatches.map((match) => (
                                <div key={match.id} className="p-3 rounded-lg bg-muted/50">
                                    <div className="flex justify-between items-center mb-1">
                                        <span className="team-name text-sm">{match.home_team}</span>
                                        <span className="text-muted-foreground text-xs">vs</span>
                                        <span className="team-name text-sm">{match.away_team}</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground text-center">
                                        {format(parseISO(match.match_date), 'dd. MMMM yyyy', { locale: de })}
                                    </p>
                                </div>
                            ))}
                            {upcomingMatches.length === 0 && (
                                <p className="text-muted-foreground text-center py-4">Keine anstehenden Spiele</p>
                            )}
                        </div>
                        <Link 
                            to="/matches" 
                            className="block mt-4 text-center text-primary hover:underline text-sm font-medium"
                            data-testid="view-all-matches"
                        >
                            Alle Spiele anzeigen
                        </Link>
                    </CardContent>
                </Card>

                {/* Recent Results */}
                <Card className="card-hover" data-testid="recent-results">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-green-500" />
                            Letzte Resultate
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {recentResults.map((match) => (
                                <div key={match.id} className="p-3 rounded-lg bg-muted/50">
                                    <div className="flex justify-between items-center">
                                        <span className="team-name text-sm flex-1">{match.home_team}</span>
                                        <div className="px-3">
                                            <span className="text-xl font-heading tabular-nums">
                                                {match.home_score} : {match.away_score}
                                            </span>
                                        </div>
                                        <span className="team-name text-sm flex-1 text-right">{match.away_team}</span>
                                    </div>
                                </div>
                            ))}
                            {recentResults.length === 0 && (
                                <p className="text-muted-foreground text-center py-4">Noch keine Resultate</p>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* User Stats */}
                <Card className="md:col-span-2 lg:col-span-3 card-hover" data-testid="user-stats">
                    <CardContent className="p-6">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                            <div>
                                <p className="text-4xl font-heading tabular-nums text-primary">{user?.total_points || 0}</p>
                                <p className="text-sm text-muted-foreground">Gesamtpunkte</p>
                            </div>
                            <div>
                                <p className="text-4xl font-heading tabular-nums">#{userRank}</p>
                                <p className="text-sm text-muted-foreground">Aktueller Rang</p>
                            </div>
                            <div>
                                <p className="text-4xl font-heading tabular-nums">{matches.filter(m => !m.is_finished).length}</p>
                                <p className="text-sm text-muted-foreground">Offene Spiele</p>
                            </div>
                            <div>
                                <p className="text-4xl font-heading tabular-nums">{matches.filter(m => m.is_finished).length}</p>
                                <p className="text-sm text-muted-foreground">Abgeschlossene Spiele</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
